import { usePathname, useSegments } from "@-/com.npmjs.expo-router";
import { useLocale } from "@-/com.npmjs.expo-router/react-navigation";
import { Platform, StyleSheet, useWindowDimensions, View } from "@-/com.npmjs.react-native";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import { RAIL_SIDEBAR_WIDTH } from "@-/dev.improve.haniverse.routes.nav._context";
import { PrimaryControlsStripExpansionPanel } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.expansion";
import { type ReactNode, useMemo } from "react";

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
});

/**
 * One of the landscape bars. The welcome galaxy (`/auth`) fills the whole screen instead and keeps
 * its own content clear of the insets. The pathname is read here, not by the chrome, so a
 * navigation re-renders only the bars.
 */
function LandscapeSafeAreaStrip({ height }: { height: number }) {
  const isWelcomeRoute = usePathname() === "/auth";
  return isWelcomeRoute ? null : (
    <View className="bg-background" pointerEvents="none" style={{ height }} />
  );
}

export function LandscapeSafeAreaChrome({
  children,
  overlay,
}: {
  children: ReactNode;
  /** Drawn over the whole screen, bars included, so it never moves when they come and go. */
  overlay?: ReactNode;
}) {
  const insets = useSafeAreaInsets();
  const isAppRoute = useSegments().includes("(app)");
  // Read the same rotation snapshot as the tabs and toolbar, without a selector-provider commit.
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();
  const { direction } = useLocale();

  // Size strips by the vertical insets only — never `left`/`right`, which in iOS landscape
  // carry the notch/Dynamic Island as *horizontal* clearance and would balloon the bars to
  // ~50pt if used as a height. Android landscape reports a real status-bar inset on `top` and
  // its nav bar on `bottom`; iOS landscape hides the status bar (top ≈ 0) and only reserves
  // the home indicator on `bottom`.
  //
  // Top and bottom are coupled: whenever either edge needs clearance we render both strips at
  // the same height (the larger of the two insets) so the chrome stays symmetric — iOS would
  // otherwise show a lone bottom strip with no matching top bar.
  const isMobileLandscape = Platform.OS !== "web" && windowWidth > windowHeight;
  // App scenes paint through the system edges and clear their own controls; standalone pages
  // retain their existing symmetric clearance.
  const landscapeSafeInset =
    isMobileLandscape && !isAppRoute ? Math.max(insets.top, insets.bottom) : 0;
  const isRight = direction === "rtl";
  const sidebarWidth = RAIL_SIDEBAR_WIDTH + (isRight ? insets.right : insets.left);

  // The expansion strip slides down from under the top safe-area bar, inset past the
  // sidebar so it covers only the scene to the side of it (the sidebar hosts the toggle).
  // `zIndex`/`elevation` keep it above the drawer scene, which on Android paints with its
  // own elevation regardless of sibling document order.
  const expansionPanelStyle = useMemo(
    () => ({
      elevation: 8,
      top: 0,
      zIndex: 8,
      ...(isRight ? { left: 0, right: sidebarWidth } : { left: sidebarWidth, right: 0 }),
    }),
    [isRight, sidebarWidth],
  );

  return (
    <View className="flex-1" pointerEvents="box-none" style={styles.root}>
      {landscapeSafeInset > 0 ? <LandscapeSafeAreaStrip height={landscapeSafeInset} /> : null}
      <View className="flex-1" pointerEvents="box-none" style={styles.root}>
        {children}
        {isMobileLandscape ? (
          <View className="absolute" pointerEvents="box-none" style={expansionPanelStyle}>
            <PrimaryControlsStripExpansionPanel />
          </View>
        ) : null}
      </View>
      {landscapeSafeInset > 0 ? <LandscapeSafeAreaStrip height={landscapeSafeInset} /> : null}
      {overlay}
    </View>
  );
}
